import React, { useContext, useEffect, useState } from 'react';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import useFetch from 'use-http';
import DialogList from 'src/dialogs';
import { DocumentTypes } from 'src/models/DocumentTypes';
import moment from 'moment';
import DocumentManagement from 'src/components/layout/DocumentManagement';
import ConfirmDeleteDocDialog from './ConfirmDeleteDocDialog';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import { FormEvent } from 'react';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';

interface IReportDocumentProps {
    id: string;
    hasOwnership: boolean;
    tractType?: 'Mineral' | 'Surface';
    horizonId?: number;
    uploadReportFileCallback?: (files: File[]) => void;
}

// TODO: For Testing, remove later
const ReportDocuments: React.FC<IReportDocumentProps> = (props) => {
    const { get, response } = useFetch();
    const [loading, setLoading] = React.useState(false);
    const [morOutputType, setMOROutputType] = useState(1);
    const [contractType, setContractType] = useState<string | number>(1);
    const [tractPrefix, setTractPrefix] = useState('');
    const [uploadMOR, setuploadMOR] = useState<boolean>(false);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

    const lTypes = [
        { value: 1, label: 'Oil and Gas Lease' },
        { value: 2, label: 'Solar Lease' },
        { value: 3, label: 'Surface Waiver' },
        { value: 4, label: 'Battery Storage' },
        { value: 5, label: 'Brine Lease' }
    ];

    useEffect(() => {
        switch (props.tractType) {
            case 'Surface':
                setTractPrefix('Surface');
                break;
            case 'Mineral':
            default:
                setTractPrefix('Mineral');
                break;
        }
    }, []);

    useEffect(() => {
        if (filesToUpload?.length > 0) {
            props.uploadReportFileCallback(filesToUpload);
        }
    }, [filesToUpload]);

    const handleUploadMORChange = (e: FormEvent<HTMLInputElement>) => {
        setuploadMOR(e.currentTarget.checked);
    };

    const onUpdateContractType = (e: FormEvent<HTMLSelectElement>) => {
        setContractType(e.currentTarget.value);
    };

    const getReportFile = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        if (!loading && props.hasOwnership) {
            setLoading(true);

            await get(`v1.0/reports/GetReport/${morOutputType}/globalId/${props.id}/contractType/${contractType}`);
            let fileBlob: BlobPart = undefined;
            if (response.ok) {
                await response.blob().then((blob) => {
                    fileBlob = blob;
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.getElementById('rep') as HTMLAnchorElement;
                    a.href = blobUrl;
                    a.download = morOutputType === 1 ? `${tractPrefix}-${props.id}-MOR.xlsx` : `${tractPrefix}-${props.id}-MOR.pdf`;
                    a.click();
                    window.URL.revokeObjectURL(blobUrl);
                });
            }
            if (uploadMOR) {
                let uploadFile = new File([fileBlob], morOutputType === 1 ? `${tractPrefix}-${props.id}-MOR.xlsx` : `${tractPrefix}-${props.id}-MOR.pdf`);
                setFilesToUpload([uploadFile]);
            }
            setLoading(false);
        }
    };

    return (
        <>
            <a style={{ display: 'hidden' }} id="rep" />
            <div style={{ display: 'flex' }}>
                <div
                    onClick={getReportFile}
                    className={`dlc-form-button-default medium primary ${loading || !props.hasOwnership ? 'disabled' : ''}`}
                >
                    {`${tractPrefix} Ownership Report`}
                </div>
                <span style={{ position: 'relative', display: 'inline-block', overflow: 'hidden', width: loading ? 'auto' : 0, marginLeft: '.5rem' }}>
                    <i className="fas fa-spinner fa-spin" />
                </span>
                <div style={{ position: 'relative' }}>
                    <div>
                        <RHFCheckbox
                            style={{ verticalAlign: 'middle' }}
                            label={'Automatically upload'}
                            name={'uploadToS3'}
                            onClick={handleUploadMORChange}
                        />
                    </div>
                    <div style={{ position: 'absolute', top: '50%', transform: 'translateY(30%)', display: 'flex' }}>
                        <input type="radio" id="excel" name="mor_output_type" onChange={() => setMOROutputType(1)} checked={morOutputType === 1} />
                        <label htmlFor="excel">Excel</label>

                        <div style={{ margin: '0 1rem' }}>|</div>

                        <input type="radio" id="pdf" name="mor_output_type" onChange={() => setMOROutputType(2)} checked={morOutputType === 2} />
                        <label htmlFor="pdf">PDF</label>
                    </div>
                </div>
            </div>
            <div>
                <RHFSelect
                    style={{ maxWidth: '16.5rem', transform: 'translateY(.8rem)' }}
                    options={lTypes}
                    name="contractType"
                    label="Contract Type"
                    onChange={(e) => onUpdateContractType(e)}
                />
            </div>
        </>
    );
};

type fileDataType = {
    id: number;
    originalFileName: string;
    fileSize: number;
    fileExtension: string;
    fileType: string;
    s3FileId: number;
    s3KeyName: string;
    uploadDate: Date;
    note: string;
    docType: string;
};

interface ITractDetailDocsProps {
    showDialog: (dialogName: string) => void;
    hideDialog: () => void;
    hasOwnership: boolean;
    tractType?: 'Mineral' | 'Surface';
    horizonId?: number;
}
const TractDetailDocs = (props: ITractDetailDocsProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const [fileData, setFileData] = useState(undefined);
    const [fileIdToDelete, setFileIdToDelete] = useState(undefined);
    const { get, request, response } = useFetch();
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

    const loadData = async () => {
        let filePath = props.tractType === 'Surface'
            ? `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/s3bucket`
            : `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}/s3bucket`;

        const data = await get(filePath);

        if (response.ok) {
            setFileData(data);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const uploadFileCallback = (files: File[]) => {
        setFilesToUpload(files);
    };

    const downloadFile = async (keyName: string, index: number, fileName: string) => {
        await get(`/v1.0/s3bucket/${keyName}`);
        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById(`a_${index}`) as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            });
        }
    };

    const deleteFile = async (s3FileId: number) => {
        if (!isNaN(s3FileId)) {
            setFileIdToDelete(s3FileId);
            props.showDialog(DialogList.DELETE_DOC);
        }
    };

    const deleteFileConfirmed = async () => {
        props.hideDialog();
        await request.delete(`/v1.0/s3bucket/${fileIdToDelete}`);
        setFileIdToDelete(undefined);
        loadData();
    };

    const deleteFileCanceled = () => {
        props.hideDialog();
        setFileIdToDelete(undefined);
    };

    const docType = (docTypeValue: string) => {
        const numDocType = parseInt(docTypeValue, 10);
        if (numDocType) {
            const type = DocumentTypes.find((dc) => dc.id === numDocType) || { name: 'Bad Doc Type' };
            return type;
        }
        return { name: 'No Doc Type Set' };
    };

    const displayFiles = () => {
        if (fileData) {
            return fileData.map((file: fileDataType, index: number) => {
                return (
                    <div
                        key={index}
                        style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid darkgrey' }}
                        className="mb-2"
                    >
                        <div style={{ width: '10%', textAlign: 'center' }}>{moment(file.uploadDate).format('MM-DD-YYYY')}</div>
                        <div style={{ width: '30%', textAlign: 'center' }}>{file.originalFileName}</div>
                        <div style={{ width: '10%', textAlign: 'center' }}>{file.fileSize / 1000 || 'NA'}Kb</div>
                        <div style={{ width: '10%', textAlign: 'center' }}>{docType(file.docType).name}</div>
                        <div style={{ width: '25%', textAlign: 'center' }}>{file.note}</div>
                        <div style={{ width: '15%', textAlign: 'center' }}>
                            <span
                                onClick={() => downloadFile(file.s3KeyName, index, file.originalFileName)}
                                style={{ marginRight: '1rem', textDecoration: 'underline' }}
                                className="clickable"
                            >
                                DOWNLOAD
                            </span>
                            <span onClick={() => deleteFile(file.id)} style={{ color: 'red' }} className="clickable">X</span>
                            {/* <a onClick={() => downloadFile(file.s3KeyName, index, file.originalFileName)}>
                                <i style={{ fontSize: '1.7rem' }} className="far fa-download mr-1 clickable" />
                            </a>

                            <a onClick={() => deleteFile(file.id)} style={{ color: 'red' }}>
                                <i style={{ fontSize: '1.7rem' }} className="far fa-trash mr-1 clickable" />
                            </a> */}
                        </div>
                        {/* <button onClick={() => downloadFile(file.s3KeyName, index, file.originalFileName)}>download</button> */}
                        <a id={`a_${index}`} />
                    </div>
                );
            });
        } else {
            return <div>No Files Found</div>;
        }
    };

    const filesUploaded = () => {
        props.hideDialog();
        loadData();
    };

    const generateMOR = () => {
        return (
            <div>
                <ReportDocuments
                    id={`${appContext.clientNumber}.${appContext.projectNumber}.${appContext.fieldNumber}.${appContext.tractNumber}-${appContext.horizonNumber}`}
                    hasOwnership={props.hasOwnership}
                    tractType={props.tractType}
                    horizonId={props.horizonId}
                    uploadReportFileCallback={uploadFileCallback}
                />
            </div>
        );
    };

    return (
        <>
            <div>
                <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                    <legend className="legend">Document Management</legend>
                </fieldset>

                <div style={{ display: 'flex' }}>
                    <div className="mr-2" style={{ width: '70%' }}>
                        <div style={{ fontSize: '1.75rem' }}>Attached Files</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ width: '10%', textAlign: 'center', fontSize: '1.6rem' }}>Upload Date</div>
                            <div style={{ width: '30%', textAlign: 'center', fontSize: '1.6rem' }}>Filename</div>
                            <div style={{ width: '10%', textAlign: 'center', fontSize: '1.6rem' }}>File Size</div>
                            <div style={{ width: '10%', textAlign: 'center', fontSize: '1.6rem' }}>Doc Type</div>
                            <div style={{ width: '25%', textAlign: 'center', fontSize: '1.6rem' }}>Note</div>
                            <div style={{ width: '15%', textAlign: 'center', fontSize: '1.6rem' }}>Actions</div>
                        </div>

                        <div style={{ maxHeight: '60rem', overflow: 'auto' }}>{displayFiles()}</div>
                    </div>

                    <div style={{ borderLeft: '2px solid darkgrey' }} className="mr-2" />

                    <div>
                        <div>
                            <div style={{ fontSize: '1.75rem' }}>Generate Documents</div>
                            {generateMOR()}
                        </div>
                    </div>
                </div>
                <Button
                    className="blue-hover-button"
                    style={{ marginRight: '1rem' }}
                    text="Attach Document"
                    size="medium"
                    intent="primary"
                    onClick={() => props.showDialog(DialogList.DOCUMENT_MANAGEMENT)}
                />
            </div>
            <DocumentManagement
                filesUploaded={filesUploaded}
                context={props.tractType === 'Surface' ? 'surface tract' : 'mineral tract'}
                filesToUpload={filesToUpload}
            />
            <ConfirmDeleteDocDialog confirmDelete={deleteFileConfirmed} cancelDelete={deleteFileCanceled} />
        </>
    );
};

export default TractDetailDocs;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/TractDetailDocs.tsx